import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  getActiveOrdersAPI,
  getNextDayPromoStoreOrdersAPI,
} from '../api-services/OrderManagement.service';

const OrderManagementDataContext = createContext(null);

const initialOrdersRequest = {
  loading: false,
  data: null,
  success: null,
  errorMessage: '',
};

export function OrderManagementDataProvider({ children }) {
  const [activeOrdersData, setActiveOrdersData] = useState(initialOrdersRequest);
  const [nextDayPromoOrdersData, setNextDayPromoOrdersData] = useState(initialOrdersRequest);

  const fetchActiveOrders = useCallback(async (params) => {
    setActiveOrdersData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });

    try {
      const data = await getActiveOrdersAPI(params);

      if (data.hasError) {
        setActiveOrdersData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Failed to load orders.',
        });
        return data;
      }

      const payload = data.data || {};
      const ok = payload.success === true;

      setActiveOrdersData({
        loading: false,
        data,
        success: ok,
        errorMessage: ok ? '' : payload.message || 'Could not load orders.',
      });

      return data;
    } catch (error) {
      console.log('fetchActiveOrders catch', error);
      setActiveOrdersData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Failed to load orders.',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const fetchNextDayPromoStoreOrders = useCallback(async (params) => {
    setNextDayPromoOrdersData({
      loading: true,
      data: null,
      success: null,
      errorMessage: '',
    });

    try {
      const data = await getNextDayPromoStoreOrdersAPI(params);

      if (data.hasError) {
        setNextDayPromoOrdersData({
          loading: false,
          data: null,
          success: false,
          errorMessage: data.message || 'Failed to load next day promotion orders.',
        });
        return data;
      }

      const payload = data.data || {};
      const ok = payload.success === true;

      setNextDayPromoOrdersData({
        loading: false,
        data,
        success: ok,
        errorMessage: ok ? '' : payload.message || 'Could not load next day promotion orders.',
      });

      return data;
    } catch (error) {
      console.log('fetchNextDayPromoStoreOrders catch', error);
      setNextDayPromoOrdersData({
        loading: false,
        data: null,
        success: false,
        errorMessage: error?.message || 'Failed to load next day promotion orders.',
      });
      return { hasError: true, message: error?.message };
    }
  }, []);

  const value = useMemo(
    () => ({
      activeOrdersData,
      fetchActiveOrders,
      nextDayPromoOrdersData,
      fetchNextDayPromoStoreOrders,
    }),
    [activeOrdersData, fetchActiveOrders, nextDayPromoOrdersData, fetchNextDayPromoStoreOrders]
  );

  return (
    <OrderManagementDataContext.Provider value={value}>{children}</OrderManagementDataContext.Provider>
  );
}

export function useOrderManagementData() {
  const ctx = useContext(OrderManagementDataContext);
  if (!ctx) {
    throw new Error('useOrderManagementData must be used within OrderManagementDataProvider');
  }
  return ctx;
}
